import React, { useEffect, useState } from 'react';
import { Card, Space, Typography, Collapse, Row, Col, Divider, Table, Tag, Tooltip } from 'antd';
import moment from 'moment-timezone';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import { calculateImpressions } from '../../CampaignRequestor/CampaignRequestorUtil';
import { formatCurrency2SigFig } from '../../../../core/utils/campaigns';
import { advertiserTypeMap, renewalStatusTypes } from '../../../../core/utils/constants/constants';
import { HasAccess } from '@permify/react-role';
import { GiftFilled, CheckCircleFilled, CloseCircleOutlined } from '@ant-design/icons';
import CampaignRequestorComplianceAudiences from '../../CampaignRequestor/CampaignRequestorComplianceAudiences';

const kpiDefaultOptions = [
  {
    key: 'transactions',
    kpi: 'Transactions',
  },
  {
    key: 'signups',
    kpi: 'Sign-Ups',
  },
  {
    key: 'sessions',
    kpi: 'Sessions',
  },
];

const { Panel } = Collapse;
const { Title, Text } = Typography;

export const CampaignOrderQueueReviewDetails = ({ currentCampaignOrder, isAdvancedMode = false }) => {
  const { timezone } = useTimezone();
  const [kpis, setKpis] = useState(kpiDefaultOptions);
  const {
    name,
    description,
    startDate,
    endDate,
    budget,
    isRegulated,
    category,
    advertiserType,
    cpm,
    type,
    notes,
    campaigns,
    transactionsConfig,
    signUpConfig,
    sessionConfig,
    attributionTargets,
    geoTargets,
    measureWalkInTraffic,
    advertiser,
    renewalStatus,
    pricingModel,
    complianceAudiences,
  } = currentCampaignOrder;

  // Create hierarchical data structure: campaign headers + line items
  const budgetTableData = [];
  campaigns.forEach((campaign, campaignIndex) => {
    const lineItems = campaign.lineItems || [];

    // Calculate total budget from line items (campaign budget is sum of line item budgets)
    const totalBudget = lineItems.reduce((sum, item) => sum + (parseFloat(item.budgetTotal) || 0), 0);
    const campaignBudget = campaign.budget || totalBudget;  // Use totalBudget if campaign.budget is not set

    // Calculate campaign type labels
    const campaignTypeLabels = {
      1: 'Display',
      7: 'External',
      8: 'Video',
    };

    // Add campaign header row
    budgetTableData.push({
      key: `campaign-${campaignIndex}`,
      isCampaignHeader: true,
      campaignIndex,
      name: campaign.name || `Campaign ${campaignIndex + 1}`,
      type: campaign.type,
      typeLabel: campaignTypeLabels[campaign.type] || 'Display',
      budget: campaignBudget,
      lineItemCount: lineItems.length,
      totalImpressions: lineItems.reduce((sum, item) => {
        return sum + calculateImpressions({
          budget: parseFloat(item.budgetTotal) || 0,
          cpm: parseFloat(item.cpm) || cpm,
        });
      }, 0),
      totalBudget: totalBudget,
    });

    // Add line item rows under this campaign
    lineItems.forEach((lineItem, lineItemIndex) => {
      const budgetTotal = parseFloat(lineItem.budgetTotal) || 0;
      const budgetPercentage = campaignBudget > 0 && budgetTotal > 0
        ? ((budgetTotal / campaignBudget) * 100).toFixed(1)
        : 0;

      budgetTableData.push({
        key: `campaign-${campaignIndex}-lineitem-${lineItemIndex}`,
        isLineItem: true,
        campaignIndex,
        lineItemIndex,
        index: lineItemIndex + 1,
        name: lineItem.name || `Line Item ${lineItemIndex + 1}`,
        startDate: lineItem.startDate,
        endDate: lineItem.endDate,
        impressions: calculateImpressions({
          budget: budgetTotal,
          cpm: parseFloat(lineItem.cpm) || cpm,
        }),
        budgetTotal,
        budgetPercentage,
        valueAdd: lineItem.valueAdd,
        ...lineItem
      });
    });
  });

  // Calculate totals across all line items
  const lineItemRows = budgetTableData.filter(row => row.isLineItem);
  const totalLineItems = lineItemRows.length;
  const totalImpressions = lineItemRows.reduce((sum, item) => sum + (item.impressions || 0), 0);
  const totalBudgetUsed = lineItemRows.reduce((sum, item) => sum + (parseFloat(item.budgetTotal) || 0), 0);

  useEffect(() => {
    let updatedKpis = [...kpis];

    if (transactionsConfig) {
      updatedKpis = processTag({
        eventType: 'transactions',
        config: transactionsConfig,
        updatedKpis,
      });
    }

    if (signUpConfig) {
      updatedKpis = processTag({
        eventType: 'signups',
        config: {
          ...signUpConfig,
          tag: {
            name: signUpConfig.tag ? signUpConfig.tag.name : signUpConfig.name || '',
          },
        },
        updatedKpis,
      });
    }

    if (sessionConfig) {
      updatedKpis = processTag({
        eventType: 'sessions',
        config: sessionConfig,
        updatedKpis,
      });
    }

    setKpis(updatedKpis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCampaignOrder]);

  const processTag = ({ eventType, config, updatedKpis }) => {
    const matchedTag = updatedKpis.find((tag) => tag.key === eventType);
    const newTag = {
      ...matchedTag,
      ...config,
    };

    // Replace the matched tag with the updated tag
    const updatedTags = updatedKpis.map((tag) => (tag.key === eventType ? newTag : tag));
    return updatedTags;
  };

  return (
    <div id="campaign-order-details">
      <Collapse bordered={false} defaultActiveKey={[0]}>
        <Panel open={true} header={<Title level={5}>CAMPAIGN ORDER DETAILS</Title>}>
          <Card>
            <Row gutter={20}>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Campaign Name</Text>
                  <Text ellipsis>{name}</Text>
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Advertiser</Text>
                  <Text>{advertiser && advertiser.name}</Text>
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Channel Partner</Text>
                  <Text></Text>
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Campaign Type</Text>
                  <Text>{type}</Text>
                </Space>
              </Col>
            </Row>

            <Divider plain></Divider>

            <Row gutter={20}>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Regulated Industry</Text>
                  <Text>{isRegulated ? 'Yes' : 'No'}</Text>
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Category</Text>
                  <Text>{category}</Text>
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Advertiser Type</Text>
                  <Text>{advertiserTypeMap[advertiserType]}</Text>
                </Space>
              </Col>
            </Row>

            <Divider plain></Divider>

            <Row gutter={20}>
              {pricingModel !== 'Percentage' && (
                <Col span={4}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">CPM</Text>
                    <Text>{formatCurrency2SigFig(cpm)}</Text>
                  </Space>
                </Col>
              )}
              <Col span={4}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Campaign Budget</Text>
                  <Text>{formatCurrency2SigFig(budget)}</Text>
                </Space>
              </Col>
              {pricingModel !== 'Percentage' && (
                <Col span={4}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Impression Goal</Text>
                    <Text>
                      {calculateImpressions({
                        budget,
                        cpm,
                      }).toLocaleString()}
                    </Text>
                  </Space>
                </Col>
              )}
            </Row>

            <Row gutter={20}>
              <Col span={4}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Start Date</Text>
                  <Text>{moment(startDate).tz(timezone).format('MM/DD/YYYY')}</Text>

                  {moment(startDate).tz(timezone).isBefore(moment.tz(timezone), "day") &&
                   !['LIVE', 'LIVE_PENDING', 'LIVE_APPROVED'].includes(currentCampaignOrder?.status) && (
                    <Text type="warning">Start date is in the past</Text>
                  )}
                </Space>
              </Col>
              <Col span={4}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">End Date</Text>
                  <Text>{moment(endDate).tz(timezone).format('MM/DD/YYYY')}</Text>
                </Space>
              </Col>
            </Row>

            <Divider plain></Divider>

            <Row gutter={20}>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Description</Text>
                  <Text>{description}</Text>
                </Space>
              </Col>
              <HasAccess roles={['isTrafficker']}>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text type="secondary">Renewal Status</Text>
                    <Text>{renewalStatusTypes[renewalStatus]}</Text>
                  </Space>
                </Col>
              </HasAccess>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text type="secondary">Additional Details</Text>
                  <Text>{notes}</Text>
                </Space>
              </Col>
            </Row>

            <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 24 }}>
              {/* BUDGET TABLE */}
              <div className="requester-table-wrapper" style={{ marginTop: 20 }}>
                <div className="requester-table-header">
                  <Row>
                    <Col span={12}>
                      <Title level={5}>BUDGET ALLOCATION</Title>
                    </Col>
                  </Row>
                </div>
                <Table
                  rowKey="key"
                  columns={(() => {
                    // Base columns always visible
                    const baseColumns = [
                      {
                        title: '#',
                        key: 'index',
                        width: isAdvancedMode ? '5%' : '8%',
                        render: (_, record) => {
                          if (record.isCampaignHeader) {
                            return null;
                          }
                          return (
                            <Text style={{ fontSize: '14px', paddingLeft: '16px' }}>
                              {record.index}
                            </Text>
                          );
                        },
                      },
                      {
                        title: 'Campaign / Line Item',
                        key: 'name',
                        width: isAdvancedMode ? '20%' : '25%',
                        render: (_, record) => {
                          if (record.isCampaignHeader) {
                            return (
                              <Space>
                                <Text strong style={{ fontSize: '15px' }}>
                                  {record.name}
                                </Text>
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                  ({record.typeLabel})
                                </Text>
                              </Space>
                            );
                          }
                          return (
                            <Text style={{ fontSize: '14px', paddingLeft: '16px' }}>
                              {record.name}
                            </Text>
                          );
                        },
                      },
                      {
                        title: 'Duration',
                        key: 'duration',
                        width: isAdvancedMode ? '18%' : '20%',
                        render: (_, record) => {
                          if (record.isCampaignHeader) {
                            return (
                              <Text type="secondary" style={{ fontSize: '13px' }}>
                                {record.lineItemCount} Line Item{record.lineItemCount !== 1 ? 's' : ''}
                              </Text>
                            );
                          }
                          return (
                            <Text style={{ fontSize: '14px' }}>
                              {record.startDate && record.endDate
                                ? `${moment(record.startDate).tz(timezone).format('MM/DD/YYYY')} - ${moment(record.endDate).tz(timezone).format('MM/DD/YYYY')}`
                                : 'Not specified'}
                            </Text>
                          );
                        },
                      },
                      ...(pricingModel !== 'Percentage' ? [{
                        title: 'Imps.',
                        key: 'impressions',
                        width: isAdvancedMode ? '10%' : '12%',
                        render: (_, record) => {
                          if (record.isCampaignHeader) {
                            return (
                              <Text strong style={{ fontSize: '14px' }}>
                                {(record.totalImpressions || 0).toLocaleString()}
                              </Text>
                            );
                          }
                          return (
                            <Text style={{ fontSize: '14px' }}>
                              {(record.impressions || 0).toLocaleString()}
                            </Text>
                          );
                        },
                      }] : []),
                      {
                        title: 'Budget',
                        key: 'budget',
                        width: isAdvancedMode ? '17%' : '20%',
                        render: (_, record) => {
                          if (record.isCampaignHeader) {
                            const utilization = record.budget > 0
                              ? Math.round((record.totalBudget / record.budget) * 100)
                              : 0;
                            return (
                              <Space direction="vertical" size={2}>
                                <Text strong style={{ fontSize: '14px' }}>
                                  {formatCurrency2SigFig(record.budget)}
                                </Text>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {utilization}% allocated
                                </Text>
                              </Space>
                            );
                          }
                          return (
                            <Space size={4}>
                              <Text style={{ fontSize: '14px' }}>
                                {formatCurrency2SigFig(record.budgetTotal)}
                              </Text>
                              <Text type="secondary" style={{ fontSize: '13px' }}>
                                {record.budgetPercentage}%
                              </Text>
                            </Space>
                          );
                        },
                      },
                    ];

                    // Advanced mode columns - only visible for Ad Ops
                    const advancedColumns = [
                      {
                        title: 'Delivery',
                        key: 'markedForDelivery',
                        width: '10%',
                        align: 'center',
                        render: (_, record) => {
                          if (record.isCampaignHeader) {
                            return null;
                          }
                          return record.markedForDelivery ? (
                            <Tooltip title="Marked for Delivery">
                              <CheckCircleFilled style={{ fontSize: 18, color: '#52c41a' }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Not Marked for Delivery">
                              <CloseCircleOutlined style={{ fontSize: 18, color: '#d9d9d9' }} />
                            </Tooltip>
                          );
                        },
                      },
                      {
                        title: 'Client',
                        key: 'isOrigin',
                        width: '10%',
                        align: 'center',
                        render: (_, record) => {
                          if (record.isCampaignHeader) {
                            return null;
                          }
                          return record.isOrigin ? (
                            <Tooltip title="Client Created Line Item">
                              <CheckCircleFilled style={{ fontSize: 18, color: '#1890ff' }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Not Client Created">
                              <CloseCircleOutlined style={{ fontSize: 18, color: '#d9d9d9' }} />
                            </Tooltip>
                          );
                        },
                      },
                    ];

                    // Value Add column - always visible
                    const valueAddColumn = {
                      title: 'Value Add',
                      key: 'valueAdded',
                      width: isAdvancedMode ? '10%' : '15%',
                      align: 'center',
                      render: (_, record) => {
                        if (record.isCampaignHeader) {
                          return null;
                        }
                        return record.valueAdd ? (
                          <Tooltip title="Value Added">
                            <GiftFilled
                              style={{
                                fontSize: 20,
                                color: '#722ed1',
                              }}
                            />
                          </Tooltip>
                        ) : null;
                      },
                    };

                    // Combine columns based on advanced mode
                    return isAdvancedMode
                      ? [...baseColumns, ...advancedColumns, valueAddColumn]
                      : [...baseColumns, valueAddColumn];
                  })()}
                  dataSource={budgetTableData}
                  pagination={false}
                  className="requester-table"
                  rowClassName={(record) => {
                    if (record.isCampaignHeader) {
                      return 'campaign-header-row';
                    }
                    return '';
                  }}
                  footer={() => (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                      <Text strong>
                        {totalLineItems} Line Item{totalLineItems !== 1 ? 's' : ''}
                      </Text>
                      <Space size="large">
                        {pricingModel !== 'Percentage' && (
                          <Text>
                            <Text type="secondary">Total Impressions: </Text>
                            <Text strong>{totalImpressions.toLocaleString()}</Text>
                          </Text>
                        )}
                        <Text>
                          <Text type="secondary">Budget Used: </Text>
                          <Text strong>{formatCurrency2SigFig(totalBudgetUsed)} / {formatCurrency2SigFig(budget)}</Text>
                        </Text>
                      </Space>
                    </div>
                  )}
                />
              </div>

              {/* RETAIL TABLE */}
              <div className="requester-table-wrapper" style={{ marginTop: 20 }}>
                <div className="requester-table-header">
                  <Row>
                    <Col span={12}>
                      <Title level={5}>RETAIL STORE LOCATIONS</Title>
                    </Col>
                  </Row>
                </div>
                <Table
                  rowKey="id"
                  columns={[
                    {
                      title: 'Location Name',
                      dataIndex: 'name',
                      key: 'name',
                      ellipsis: true, // Enables ellipsis for text overflow
                      width: 200, // Set a fixed width for the column
                    },
                    {
                      title: 'Street Address',
                      dataIndex: 'formattedAddress',
                      key: 'formattedAddress',
                      render: (_, { location }) => (location && location.name) || '',
                    },
                  ]}
                  dataSource={attributionTargets || []}
                  pagination={false}
                  className="requester-table"
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2}>
                        {`${attributionTargets.length} Retail Stores`}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </div>

              {/* COMPLIANCE TARGETING - Only show for Cannabis/CBD Retail stores */}
              {(() => {
                const isCannabisCategory = Array.isArray(category)
                  ? category.includes('IAB7-5')
                  : category === 'IAB7-5';
                const showComplianceTargeting =
                  isRegulated === true &&
                  isCannabisCategory &&
                  advertiserType === 'RETAIL_LOCAL_BRICK_AND_MORTAR';

                if (!showComplianceTargeting) return null;

                return (
                  <div style={{ marginTop: 20 }}>
                    <CampaignRequestorComplianceAudiences
                      complianceAudiences={complianceAudiences || []}
                      readOnly={true}
                    />
                  </div>
                );
              })()}

              {/* KPI MEASUREMENT TABLE */}
              <div className="requester-table-wrapper">
                <div className="requester-table-header">
                  <Row>
                    <Col span={12}>
                      <Title level={5}>KPI MEASUREMENT</Title>
                    </Col>
                  </Row>
                </div>
                <Table
                  key="kpi"
                  columns={[
                    {
                      title: 'KPI',
                      dataIndex: 'kpi',
                      key: 'kpi',
                      render: (_, { kpi }) => (
                        <Tag color="green" key={kpi}>
                          {kpi}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Corresponding Tag',
                      dataIndex: 'correspondingTag',
                      key: 'correspondingTag',
                      render: (_, { tag, appId, key }) => {
                        if (tag && tag.name) {
                          return tag.name;
                        } else if (appId) {
                          return '';
                        }
                      },
                    },
                    {
                      title: 'Tag ID',
                      dataIndex: 'tagId',
                      key: 'tagId',
                      render: (_, { appId }) => {
                        if (appId) {
                          return appId;
                        }
                        return '';
                      },
                    },
                  ]}
                  dataSource={kpis}
                  pagination={false}
                  className="requester-table"
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={4}>
                        3 Tags
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </div>

              {/* WALK-IN TABLE */}
              <div className="requester-table-wrapper">
                <div className="requester-table-header">
                  <Row>
                    <Col span={12}>
                      <Title level={5}>WALK-IN MEASUREMENT</Title>
                    </Col>
                  </Row>
                </div>
                {attributionTargets.length && measureWalkInTraffic ? (
                  <Table
                    rowKey="id"
                    style={{ borderTop: '1px solid #f0f0f0' }}
                    showHeader={false}
                    columns={[
                      {
                        title: 'Location Name',
                        dataIndex: 'name',
                        key: 'name',
                        ellipsis: true, // Enables ellipsis for text overflow
                        width: 200, // Set a fixed width for the column
                      },
                      {
                        title: 'Street Address',
                        dataIndex: 'formattedAddress',
                        key: 'formattedAddress',
                        render: (_, { location }) => (location && location.name) || '',
                      },
                    ]}
                    dataSource={attributionTargets}
                    pagination={false}
                    className="requester-table"
                  />
                ) : null}
                {geoTargets.length ? (
                  <Table
                    rowKey="id"
                    style={{ borderTop: '1px solid #f0f0f0' }}
                    showHeader={false}
                    columns={[
                      {
                        title: 'Location Name',
                        dataIndex: 'name',
                        key: 'name',
                      },
                      {
                        title: 'Street Address',
                        dataIndex: 'formattedAddress',
                        key: 'formattedAddress',
                        render: (_, { location }) => (location && location.name) || '',
                      },
                    ]}
                    dataSource={geoTargets}
                    pagination={false}
                    className="requester-table"
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2}>
                          {attributionTargets.length + geoTargets.length} Locations
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                ) : (
                  <></>
                )}
              </div>
            </Space>
          </Card>
        </Panel>
      </Collapse>
    </div>
  );
};
